import { cn, WithPartial } from "@/lib/utils";
import * as Tabs from "@radix-ui/react-tabs";
import type { VariantProps } from "class-variance-authority";
import React, { HTMLProps } from "react";
import { FieldValues, useFormContext, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { currencyOptions } from "../form-fields/InputSelectField";
import TableFileField from "../form-fields/TableField";
import Tiptap from "../tiptap/TipTap";
import { Button, buttonVariants } from "../ui/button";
import { Checkbox as ShadCheckbox } from "../ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  HeadlessFormItem,
} from "../ui/form";
import { Input as ShadInput } from "../ui/input";
import { Label } from "../ui/label";
import InputSelect, { BaseInputSelectProps } from "../ui/new-input-select";
import {
  RadioGroupItem,
  RadioGroup as ShadRadioGroup,
} from "../ui/radio-group";
import { CommentRatings } from "../ui/rating";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch as ShadSwitch } from "../ui/switch"; // Assuming you have a Switch component
import { Textarea as ShadTextArea } from "../ui/textarea";
// import { UniqueImageFullScreen } from "../modal/UniqueImageFullScreen"; Do not remove, will be used soon
import { FieldOption } from "@/config/forms/general";
import { toast } from "@/hooks/use-toast";
import { LegacyMoneyAmount, MoneyAmount } from "@/lib/shared-types";
import { RefreshCcw } from "lucide-react";
import TableExemplaire from "../form-fields/TableExemplaire";
import { BadgeCell, HtmlCell } from "../table/CustomCell";
import FileTypeIcon from "../ui/FileTypeIcon";
import { MultiSelect } from "../ui/multi-select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
export interface Tab {
  value: string;
  label?: string;
  hide?: boolean;
  subtab?: readonly Tab[];
}

interface AbstractFormContextType {
  tabs: readonly Tab[];
}

const AbstractFormContext = React.createContext<
  AbstractFormContextType | undefined
>(undefined);

const useAbstractFormData = () => {
  const context = React.useContext(AbstractFormContext);
  if (!context) {
    throw new Error("useAbstractFormData must be used within an AbstractForm");
  }
  return context as AbstractFormContextType;
};

export interface AbstractFormProps<
  T extends FieldValues,
  Tabs extends readonly Tab[],
> {
  tabs: Tabs;
  defaultTab?: Tabs[number]["value"];
  children?: React.ReactNode;
  form: UseFormReturn<T>;
  classname?: string;
  model?: "cms" | "default";
  /**
   * When false, the AbstractForm will skip URL/tab validation side effects.
   * Useful to avoid transient \"tabNotFound\" toasts while some async state
   * (like feature flags) is still being resolved.
   */
  ready?: boolean;
}
/**
 * This component creates a form, and make available the hook "useAbstractFormData",
 * which gives access to the formContext passed as parameter, aswell to the list of tabs
 */
// Utility function for tab value management
const createCompositeValue = (tabValue: string, subTabValue: string) =>
  `${tabValue}-${subTabValue}`;

export const AbstractForm = <
  T extends FieldValues,
  Tabs extends readonly Tab[],
>({
  tabs,
  children,
  form,
  defaultTab = tabs[0].value,
  classname,
  model = "default",
  ready = true,
}: AbstractFormProps<T, Tabs>) => {
  const [activeTabName, setActiveTabName] =
    React.useState<Tabs[number]["value"]>(defaultTab);
  const [activeSubTabName, setActiveSubTabName] = React.useState<string | null>(
    null,
  );
  const { t } = useTranslation();

  const [searchParams, setSearchParam] = useSearchParams();

  // Memoize visible tabs to avoid recalculating on every render
  const visibleTabs = React.useMemo(
    () => tabs.filter((tab) => !tab.hide),
    [tabs],
  );

  // Memoize tab lookup map for O(1) access
  const tabMap = React.useMemo(
    () => new Map(tabs.map((tab) => [tab.value, tab])),
    [tabs],
  );

  // Validate and set active tab/subtab from URL params
  React.useEffect(() => {
    if (!ready) {
      return;
    }

    const tabParam = searchParams.get("tab");
    const subtabParam = searchParams.get("subtab");

    // Reset to default if no tab param
    if (!tabParam) {
      setActiveTabName(defaultTab);
      setActiveSubTabName(null);
      return;
    }

    // Check if tab exists and is not hidden
    const tab = tabMap.get(tabParam);
    if (tab && tab.hide) {
      setActiveTabName(defaultTab);
      setActiveSubTabName(null);
      // Reset searchParams with default tab
      setSearchParam({
        ...Object.fromEntries(searchParams),
        tab: defaultTab,
      });

      toast({
        title: t("tabNotFound"),
        description: t("tabNotFoundDescription"),
        variant: "error",
        duration: 1000,
      });
      return;
    }

    // Tab is valid, set it as active
    setActiveTabName(tabParam);

    // Handle subtab validation
    if (subtabParam) {
      const validSubTab = tab?.subtab?.find(
        (st) => st.value === subtabParam && !st.hide,
      );

      if (validSubTab) {
        setActiveSubTabName(subtabParam);
      } else {
        // Invalid subtab, clear it
        setActiveSubTabName(null);
        // Clean up URL if needed
        if (searchParams.get("subtab") === subtabParam) {
          const newParams = new URLSearchParams(searchParams);
          newParams.delete("subtab");
          setSearchParam(Object.fromEntries(newParams));
        }
      }
    } else {
      // No subtab param, but check if we need to clean up
      if (searchParams.get("subtab")) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("subtab");
        setSearchParam(Object.fromEntries(newParams));
      }
      setActiveSubTabName(null);
    }
  }, [searchParams, tabMap, defaultTab, setSearchParam, t, ready]);

  // Memoize class names based on model
  const classNameMainDiv = React.useMemo(() => {
    if (model === "cms") {
      return "";
    }
    return "grid grid-cols-[auto,1fr] gap-x-16 w-full";
  }, [model]);

  const classNameTabsList = React.useMemo(() => {
    if (model === "cms") {
      return "mb-8";
    }
    return "flex-col h-fit justify-start p-2 items-start gap-1 inline-flex border rounded-xl border-[hsl(220_17%_93%)] bg-[hsl(210_24%,_98%)]";
  }, [model]);

  const classNameTabsTrigger = React.useMemo(() => {
    if (model === "cms") {
      return "";
    }
    return "self-stretch h-9 px-3 py-2 rounded-md justify-start items-center gap-2 inline-flex data-[state=active]:text-white text-gray-500";
  }, [model]);

  const classNameTabValue = React.useMemo(() => {
    if (model === "cms") {
      return "font-medium font-['Instrument Sans']";
    }
    return "font-semibold font-['Inter']";
  }, [model]);

  // Memoized helper to check if a tab has an active subtab
  const hasActiveSubTabForTab = React.useCallback(
    (tab: Tab): boolean => {
      if (!activeSubTabName || !tab.subtab) {
        return false;
      }
      return tab.subtab.some((st) => st.value === activeSubTabName && !st.hide);
    },
    [activeSubTabName],
  );

  // Memoized variant getters with proper typing
  type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];

  const getMainTabVariant = React.useCallback(
    (tab: Tab): NonNullable<ButtonVariant> => {
      const hasActiveSubTab = hasActiveSubTabForTab(tab);
      const isActive = activeTabName === tab.value;

      if (model === "cms") {
        return hasActiveSubTab
          ? "cms_tabs"
          : isActive
            ? "cms_tabs_active"
            : "cms_tabs";
      }

      return hasActiveSubTab
        ? "not_selected"
        : isActive
          ? "default"
          : "not_selected";
    },
    [activeTabName, hasActiveSubTabForTab, model],
  );

  const getSubTabVariant = React.useCallback(
    (subTab: Tab): NonNullable<ButtonVariant> => {
      const isActive = activeSubTabName === subTab.value;
      if (model === "cms") {
        return isActive ? "cms_tabs_active" : "cms_tabs";
      }
      return isActive ? "default" : "not_selected";
    },
    [activeSubTabName, model],
  );

  // Memoized className getter for main tabs
  const getMainTabClassName = React.useCallback(
    (tab: Tab): string => {
      const hasActiveSubTab = hasActiveSubTabForTab(tab);
      if (hasActiveSubTab) {
        return cn(classNameTabsTrigger, "!text-primary");
      }
      return classNameTabsTrigger;
    },
    [hasActiveSubTabForTab, classNameTabsTrigger],
  );

  // Compute the active tab value for Radix UI Tabs
  const activeTabValue = React.useMemo(() => {
    if (activeSubTabName) {
      return createCompositeValue(activeTabName, activeSubTabName);
    }
    return activeTabName;
  }, [activeTabName, activeSubTabName]);

  // Memoized handlers to avoid recreating on every render
  const handleMainTabClick = React.useCallback(
    (tabValue: string) => {
      setActiveTabName(tabValue);
      setActiveSubTabName(null);
      setSearchParam({
        ...Object.fromEntries(searchParams),
        tab: tabValue,
      });
    },
    [searchParams, setSearchParam],
  );

  const handleSubTabClick = React.useCallback(
    (tabValue: string, subTabValue: string) => {
      setActiveTabName(tabValue);
      setActiveSubTabName(subTabValue);
      setSearchParam({
        ...Object.fromEntries(searchParams),
        tab: tabValue,
        subtab: subTabValue,
      });
    },
    [searchParams, setSearchParam],
  );

  return (
    <div className={cn("pt-8 px-8 max-w-screen-2xl mx-auto", classname)}>
      <AbstractFormContext.Provider
        value={{
          tabs,
        }}
      >
        <Tabs.Root defaultValue={defaultTab} value={activeTabValue} asChild>
          <div className={classNameMainDiv}>
            <Tabs.TabsList className={classNameTabsList}>
              {visibleTabs.flatMap((tab) => {
                // Determine the trigger value: use composite if subtab is active, otherwise use tab value
                // This ensures Radix UI recognizes the main tab as active when a subtab is selected
                const hasActiveSubTab = hasActiveSubTabForTab(tab);
                const mainTabTriggerValue = hasActiveSubTab
                  ? createCompositeValue(tab.value, activeSubTabName!)
                  : tab.value;

                const hasSubTabs = tab.subtab && tab.subtab.length > 0;
                const visibleSubTabs = hasSubTabs
                  ? tab.subtab!.filter((subTab) => !subTab.hide)
                  : [];

                const mainTab = (
                  <Tabs.TabsTrigger
                    asChild
                    value={mainTabTriggerValue}
                    key={tab.value}
                  >
                    {
                      <Button
                        variant={getMainTabVariant(tab)}
                        className={cn(
                          getMainTabClassName(tab),
                          hasSubTabs && "disabled:opacity-100",
                        )}
                        onClick={
                          hasSubTabs
                            ? undefined // Disable click for tabs with subtabs
                            : () => handleMainTabClick(tab.value)
                        }
                        disabled={hasSubTabs}
                      >
                        <p className={classNameTabValue}>
                          {t(tab.label ?? tab.value)}
                        </p>
                      </Button>
                    }
                  </Tabs.TabsTrigger>
                );

                // Render subtabs if they exist
                if (visibleSubTabs.length > 0) {
                  const subTabs = visibleSubTabs.map((subTab) => {
                    const compositeValue = createCompositeValue(
                      tab.value,
                      subTab.value,
                    );
                    return (
                      <Tabs.TabsTrigger
                        asChild
                        value={compositeValue}
                        key={compositeValue}
                      >
                        {
                          <Button
                            variant={getSubTabVariant(subTab)}
                            className={cn(classNameTabsTrigger, "ml-6")}
                            onClick={() =>
                              handleSubTabClick(tab.value, subTab.value)
                            }
                          >
                            <p className={classNameTabValue}>
                              {t(subTab.label ?? subTab.value)}
                            </p>
                          </Button>
                        }
                      </Tabs.TabsTrigger>
                    );
                  });

                  return [mainTab, ...subTabs];
                }

                return [mainTab];
              })}
            </Tabs.TabsList>
            <Form {...form}>
              <div className="w-full min-w-0 col-start-2 col-span-1">
                {children}
              </div>
            </Form>
          </div>
        </Tabs.Root>
      </AbstractFormContext.Provider>
    </div>
  );
};

export type FormTabprops = (
  | { showHeader: false; headerExtras?: never }
  | { showHeader?: true; headerExtras?: React.ReactNode }
) &
  (
    | ({
        isForm?: true | undefined;
      } & HTMLProps<HTMLFormElement>)
    | ({
        isForm: false;
      } & HTMLProps<HTMLDivElement>)
  );

export const Tab = ({
  className,
  showHeader,
  value,
  children,
  isForm,
  headerExtras,
  ...props
}: React.ComponentProps<typeof Tabs.Content> & FormTabprops) => {
  const { tabs } = useAbstractFormData();

  // Resolve main tab / sub tab from the provided value (supports composite values "main-sub")
  const { relatedTab, relatedSubTab } = React.useMemo(() => {
    // First try direct match (main tab)
    const mainTab = tabs.find((t) => t.value === value);
    if (mainTab) {
      return { relatedTab: mainTab, relatedSubTab: undefined };
    }

    // Try composite value: "main-sub"
    const [main, ...rest] = value.split("-");
    if (!main || rest.length === 0) {
      return { relatedTab: undefined, relatedSubTab: undefined };
    }

    const subValue = rest.join("-");
    const parentTab = tabs.find((t) => t.value === main);
    const subTab = parentTab?.subtab?.find((st) => st.value === subValue);

    return { relatedTab: parentTab, relatedSubTab: subTab };
  }, [tabs, value]);

  const headerLabel =
    relatedSubTab?.label ??
    (relatedSubTab ? `${relatedSubTab.value}Label` : undefined) ??
    relatedTab?.label ??
    (relatedTab ? `${relatedTab.value}Label` : undefined);

  return (
    relatedTab && (
      <Tabs.Content
        className={cn("w-full flex flex-col justify-start items-center gap-5")}
        value={value}
      >
        {showHeader !== false && (
          <div className="flex justify-between items-center gap-2 flex-wrap w-full border-b pb-6">
            <p className="text-[#0c111d] text-lg font-semibold font-['Raleway'] leading-normal">
              {headerLabel}
            </p>
            {headerExtras}
          </div>
        )}
        {isForm !== false ? (
          <form
            className={cn("flex flex-row w-full gap-8", className)}
            {...(props as HTMLProps<HTMLFormElement>)}
          >
            {children}
          </form>
        ) : (
          <div
            className={cn("flex flex-row w-full gap-8", className)}
            {...props}
          >
            {children}
          </div>
        )}
      </Tabs.Content>
    )
  );
};

export const Col = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div
      className={cn("flex flex-col gap-5 w-full", className)}
      {...props}
      ref={ref}
    >
      {children}
    </div>
  );
});
Col.displayName = "AbstractFormCol";

export const Row = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  return (
    <div
      className={cn("flex flex-row gap-6 h-fit w-full items-center", className)}
      {...props}
      ref={ref}
    >
      {children}
    </div>
  );
});
Row.displayName = "AbstractFormRow";

export type PartialFormFieldProps = Partial<
  React.ComponentProps<typeof FormField>
>;

export const Text = React.forwardRef<
  React.ElementRef<typeof ShadInput>,
  React.ComponentPropsWithoutRef<typeof ShadInput> & {
    name: string;
    type?: string;
    field?: PartialFormFieldProps;
    visible?: boolean;
    sublabel?: string;
    corner?: React.ReactNode | (() => React.ReactNode);
  }
>(
  (
    { name, type, field, label, sublabel, visible = true, corner, ...props },
    ref,
  ) => {
    const form = useFormContext();
    const { t } = useTranslation();

    if (!visible) {
      return null;
    }

    const displayLabel = label ?? t(name);
    const displaySublabel = sublabel ? t(sublabel) : undefined;
    const cornerComponent = typeof corner === "function" ? corner() : corner;

    return (
      <FormField
        defaultValue=""
        name={name}
        {...form}
        {...field}
        render={({ field }) => (
          <FormItem>
            <FormControl>
              {corner ? (
                <div className="w-full">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex flex-col gap-1">
                      <Label
                        htmlFor={name}
                        className="flex text-start text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                      >
                        {displayLabel}
                      </Label>
                      {displaySublabel && (
                        <p className="text-primary text-[12px] font-normal font-['Raleway'] leading-normal">
                          {displaySublabel}
                        </p>
                      )}
                    </div>
                    {cornerComponent && <div>{cornerComponent}</div>}
                  </div>
                  <ShadInput
                    id={name}
                    type={type ?? "text"}
                    {...field}
                    ref={ref}
                    {...props}
                  />
                </div>
              ) : (
                <ShadInput
                  id={name}
                  label={displayLabel}
                  sublabel={displaySublabel}
                  type={type ?? "text"}
                  {...field}
                  ref={ref}
                  {...props}
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  },
);

Text.displayName = "AbstractFormText";

export const Switch = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    name: string;
    switchProps?: React.ComponentProps<typeof ShadSwitch>;
    field?: PartialFormFieldProps;
    visible?: boolean;
  } & LabelProps
>(
  (
    {
      name,
      label,
      dispLabel,
      className,
      field,
      switchProps,
      visible = true,
      ...props
    },
    ref,
  ) => {
    const form = useFormContext();
    const { t } = useTranslation();

    if (!visible) {
      return null;
    }

    return (
      <FormField
        {...form}
        {...field}
        name={name}
        render={({ field }) => (
          <HeadlessFormItem>
            <FormControl>
              <div
                className={cn(
                  "flex flex-row items-center gap-2 w-full",
                  className,
                )}
                ref={ref}
                {...props}
              >
                <ShadSwitch
                  id={name}
                  {...switchProps}
                  {...field}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label
                  htmlFor={field.name}
                  className="text-slate-700 w-full text-left text-sm font-medium font-['Raleway'] leading-normal"
                >
                  {dispLabel !== false && (label ?? t(name))}
                </Label>
              </div>
            </FormControl>
          </HeadlessFormItem>
        )}
      />
    );
  },
);

Switch.displayName = "AbstractFormSwitch";

export const TextArea = React.forwardRef<
  React.ElementRef<typeof ShadTextArea>,
  React.ComponentPropsWithoutRef<typeof ShadTextArea> & {
    name: string;
    field?: PartialFormFieldProps;
    visible?: boolean;
  }
>(({ name, field, visible = true, ...props }, ref) => {
  const form = useFormContext();
  const { t } = useTranslation();

  if (!visible) {
    return null;
  }

  return (
    <FormField
      defaultValue=""
      name={name}
      {...form}
      {...field}
      render={({ field }) => (
        <HeadlessFormItem>
          <FormControl>
            <div className="flex flex-col gap-0">
              <Label
                htmlFor={name}
                className="flex text-start mb-1.5 text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
              >
                {t(name)}
              </Label>
              <ShadTextArea id={name} {...field} ref={ref} {...props} />
            </div>
          </FormControl>
        </HeadlessFormItem>
      )}
    />
  );
});

export type LabelProps = { label?: string; dispLabel?: boolean };

export const Rating = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    name: string;
    field?: PartialFormFieldProps;
    visible?: boolean;
  } & LabelProps
>(({ name, field, visible = true, ...props }, ref) => {
  const form = useFormContext();
  const { t } = useTranslation();

  if (!visible) {
    return null;
  }

  return (
    <FormField
      {...form}
      {...field}
      name={name}
      render={({ field }) => (
        <HeadlessFormItem>
          <FormControl>
            <div className="flex flex-col gap-1" ref={ref} {...props}>
              <Label
                htmlFor={field.name}
                className="text-slate-700 text-left text-sm font-medium font-['Raleway'] leading-normal"
              >
                {t(name)}
              </Label>
              <CommentRatings
                rating={field.value ?? 0}
                onRatingChange={field.onChange}
              />
            </div>
          </FormControl>
        </HeadlessFormItem>
      )}
    />
  );
});

Rating.displayName = "AbstractFormRating";

export const Checkbox = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    name: string;
    align?: boolean; // to align when it's next to an input text with a label
    checkbox?: React.ComponentProps<typeof ShadCheckbox>;
    field?: PartialFormFieldProps;
    visible?: boolean;
  } & LabelProps
>(
  (
    {
      name,
      dispLabel,
      label,
      className,
      field,
      align,
      checkbox,
      visible = true,
      ...props
    },
    ref,
  ) => {
    const form = useFormContext();
    const { t } = useTranslation();

    if (!visible) {
      return null;
    }

    return (
      <FormField
        {...form}
        {...field}
        name={name}
        render={({ field }) => (
          <HeadlessFormItem>
            <FormControl>
              <div
                className={cn(
                  "flex flex-row items-center gap-2 w-full",
                  align === true && "pt-6",
                  className,
                )}
                ref={ref}
                {...props}
              >
                <ShadCheckbox
                  id={name}
                  {...checkbox}
                  {...field}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label
                  htmlFor={field.name}
                  className="text-slate-700 w-full text-left text-sm font-medium font-['Raleway'] leading-normal"
                >
                  {dispLabel !== false && (label ?? t(name))}
                </Label>
              </div>
            </FormControl>
          </HeadlessFormItem>
        )}
      />
    );
  },
);

Checkbox.displayName = "Checkbox";

/**
 * Checks if HTML content is effectively empty (only contains empty tags or whitespace)
 * Handles cases like <p></p>, <p><br></p>, <p style="..." class="..."></p>, etc.
 */
const isEmptyHTML = (html: string | null | undefined): boolean => {
  if (!html || html.trim() === "") return true;

  // Create a temporary DOM element to parse and check the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Get text content and check if it's empty or only whitespace
  const textContent = tempDiv.textContent || tempDiv.innerText || "";
  return textContent.trim() === "";
};

/**
 * Normalizes HTML content: converts empty HTML to empty string
 */
const normalizeHTML = (html: string | null | undefined): string => {
  if (!html) return "";
  return isEmptyHTML(html) ? "" : html;
};

export const RichText = React.memo(
  React.forwardRef<
    React.ElementRef<typeof Tiptap>,
    Partial<React.ComponentPropsWithoutRef<typeof Tiptap>> & {
      name: string;
      field?: PartialFormFieldProps;
      computeAction?: (data: any) => string;
      visible?: boolean;
    }
  >(({ name, label, field, computeAction, visible = true, ...props }, ref) => {
    const form = useFormContext();
    const { t } = useTranslation();

    if (!visible) {
      return null;
    }

    const handleComputeAction = (
      data: any,
      onChange: (value: string) => void,
    ) => {
      if (computeAction) {
        const value = computeAction(data);
        // Normalize empty HTML to empty string
        onChange(normalizeHTML(value));
      }
    };

    return (
      <FormField
        {...form}
        {...field}
        name={name}
        render={({ field }) => (
          <HeadlessFormItem>
            <FormControl>
              <div>
                <Tiptap
                  {...field}
                  ref={ref}
                  label={label ?? t(name)}
                  onChange={(e: any) => {
                    // Normalize empty HTML to empty string
                    const normalizedValue = normalizeHTML(e);
                    field.onChange(normalizedValue);
                  }}
                  description={normalizeHTML(form.getValues(field.name))}
                  {...props}
                />
                {computeAction && (
                  <RefreshCcw
                    type="button"
                    onClick={() =>
                      handleComputeAction(form.getValues(), field.onChange)
                    }
                    className="w-4 h-4 text-gray-500 cursor-pointer mt-1"
                  />
                )}
              </div>
            </FormControl>
          </HeadlessFormItem>
        )}
      />
    );
  }),
);

const defaultCurrencyValue = JSON.stringify({ currency: "EUR", amount: "" });

export const LegacySelectCurrency = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    name: string;
    select?: Partial<BaseInputSelectProps>;
    field?: PartialFormFieldProps;
    inputClassName?: string;
    visible?: boolean;
  } & LabelProps
>(
  (
    {
      name,
      label,
      className,
      field,
      dispLabel,
      inputClassName,
      visible = true,
      ...props
    },
    ref,
  ) => {
    const form = useFormContext();

    const { t } = useTranslation();

    if (!visible) {
      return null;
    }

    return (
      <FormField
        {...form}
        {...field}
        name={name}
        render={({ field }) => (
          <HeadlessFormItem>
            <FormControl>
              <div
                className={cn("flex flex-col gap-1 items-start", className)}
                ref={ref}
                {...props}
              >
                <Label
                  htmlFor={field.name}
                  className="text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                >
                  {dispLabel !== false && (label ?? t(name))}
                </Label>
                <InputSelect
                  selectPlaceholder="currency"
                  className={cn("w-fit", inputClassName)}
                  input={{ className: cn("w-32", inputClassName) }}
                  options={currencyOptions}
                  id={name}
                  select={{ defaultValue: "EUR" }}
                  getSelect={(value?: string) => {
                    if (!value) return "EUR";
                    try {
                      const parsed = JSON.parse(value) as LegacyMoneyAmount;
                      return parsed?.currency ?? defaultCurrencyValue;
                    } catch {
                      return "EUR";
                    }
                  }}
                  getInput={(value?: string) => {
                    if (!value) return "";

                    try {
                      const parsed = JSON.parse(value) as LegacyMoneyAmount;
                      return parsed?.amount ?? "";
                    } catch {
                      return "";
                    }
                  }}
                  defaultVal={field.value ?? defaultCurrencyValue}
                  onValueChange={(v) => {
                    field.onChange(v);
                  }}
                  constructValue={({ select, input }) => {
                    return JSON.stringify({
                      amount: input ?? "",
                      currency: select ?? "EUR",
                    });
                  }}
                />
              </div>
            </FormControl>
          </HeadlessFormItem>
        )}
      />
    );
  },
);

LegacySelectCurrency.displayName = "AbstractFormLegacySelectCurrency";

export const SelectCurrency = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    name: string;
    select?: Partial<BaseInputSelectProps>;
    field?: PartialFormFieldProps;
    visible?: boolean;
  } & LabelProps
>(
  (
    { name, label, className, field, dispLabel, visible = true, ...props },
    ref,
  ) => {
    const form = useFormContext();

    const { t } = useTranslation();
    if (!visible) {
      return null;
    }
    return (
      <FormField
        {...form}
        {...field}
        name={name}
        render={({ field }) => (
          <HeadlessFormItem>
            <FormControl>
              <div
                className={cn("flex flex-col gap-1 items-start", className)}
                ref={ref}
                {...props}
              >
                <Label
                  htmlFor={field.name}
                  className="text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                >
                  {dispLabel !== false && (label ?? t(name))}
                </Label>
                <InputSelect
                  selectPlaceholder="currency"
                  input={{ className: "w-32", type: "number", step: "any" }}
                  options={currencyOptions}
                  id={name}
                  select={{ defaultValue: "EUR" }}
                  getSelect={(value: Partial<MoneyAmount> | undefined) => {
                    if (!value) return "EUR";
                    return value.currency ?? "EUR";
                  }}
                  getInput={(value: Partial<MoneyAmount> | undefined) => {
                    if (!value) return "";
                    if (value.amount == undefined) return "";
                    return (value.amount / 100).toString();
                  }}
                  defaultVal={field.value ?? defaultCurrencyValue}
                  onValueChange={(v) => {
                    field.onChange(v);
                  }}
                  constructValue={({ select, input }) => {
                    const parsedInput = parseFloat(input);

                    return {
                      amount: Number.isNaN(parsedInput)
                        ? undefined
                        : Math.round(parsedInput * 100),
                      currency: select ?? "EUR",
                    };
                  }}
                />
              </div>
            </FormControl>
          </HeadlessFormItem>
        )}
      />
    );
  },
);

SelectCurrency.displayName = "AbstractFormSelectCurrency";

export const OthersFilesTable = ({
  docId,
  name,
  label,
  refreshFiles,
  setRefreshFiles,
  visible = true,
}: {
  docId: string;
  name: string;
  label?: string;
  refreshFiles?: boolean;
  setRefreshFiles?: (refresh: boolean) => void;
  visible?: boolean;
}) => {
  const { t } = useTranslation();

  if (!visible) {
    return null;
  }
  return (
    <TableFileField
      docId={docId}
      refreshFiles={refreshFiles}
      setRefreshFiles={setRefreshFiles}
      config={{
        name: name,
        type: "table-file",
        label: label ?? t(name),

        className: "w-full",

        table: {
          columns: [
            {
              header: t("fichierDiversTitreColonne"),
              accessorKey: "title",
              cell: (props) => {
                return (
                  <div className="flex justify-between items-center">
                    <div className="w-[60px] h-[40px] flex justify-center items-center mr-2">
                      <FileTypeIcon
                        mimetype={props.row?.original.mimetype ?? ""}
                        publicUrl={
                          props.row?.original.mimetype === "pdf"
                            ? props.row?.original.urls.original
                            : props.row?.original.urls.large
                        }
                        smallImageUrl={
                          "small" in props.row.original.urls
                            ? props.row.original.urls.small
                            : ""
                        }
                        width={40}
                        height={40}
                      />
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger
                          className="bg-transparent"
                          onClick={(e) => e.preventDefault()}
                        >
                          <p className="truncate w-[150px] bg-transparent">
                            {props.row.original.title}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="text-center text-black"
                        >
                          <p>{props.row.original.title}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                );
              },
            },
            {
              header: t("fichierDiversDateCreationColonne"),
              id: "createdAt",
              accessorKey: "createdAt",
              enableSorting: true,
            },
            {
              header: t("extensionColumnTitle"),
              accessorKey: "mimetype",
              enableSorting: false,
              cell: (props) => {
                return (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger
                        className="bg-transparent"
                        onClick={(e) => e.preventDefault()}
                      >
                        <p className="font-bold truncate bg-transparent">
                          .{props.getValue()}
                        </p>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        className="text-center text-black"
                      >
                        <p>{props.getValue()}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              },
            },
            {
              header: t("fileSizeColumnTitle"),
              accessorKey: "weight",
              cell: (props) => {
                return (
                  <BadgeCell value={`${props.getValue()} MB`} variant="draft" />
                );
              },
            },
            {
              header: t("fichierDiversDescriptionColonne"),
              accessorKey: "description",
              enableSorting: false,
              cell: (props) => {
                return (
                  <div className="w-[150px] text-wrap">
                    {props.row.original.description}
                  </div>
                );
              },
            },
          ],
          actions: [
            { name: "edit", label: t("fichierDiversModifierAction") },
            { name: "share", label: t("fichierDiversPartagerAction") },
            { name: "download", label: t("fichierDiversTelechargerAction") },
            { name: "delete", label: t("fichierDiversSupprimerAction") },
          ],
        },
      }}
    />
  );
};

export const ExemplairesTable = ({
  docId,
  name,
  label,
  visible = true,
}: {
  docId: string;
  name: string;
  label?: string;
  visible?: boolean;
}) => {
  const { t } = useTranslation();

  if (!visible) {
    return null;
  }
  return (
    <TableExemplaire
      idMultipleprint={docId}
      config={{
        name: name,
        type: "table-exemplaire",
        label: label ?? t(name),
        className: "w-full",
        table: {
          columns: [
            {
              header: "",
              id: "attachment",
            },
            {
              header: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
              accessorKey: "exemplaireDansEstampesMultiplesDescriptionNumero",
            },
            {
              header: t(
                "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
              ),
              accessorKey:
                "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
            },
            {
              header: t(
                "exemplaireDansEstampesMultiplesDescriptionLocalisation",
              ),
              accessorKey:
                "exemplaireDansEstampesMultiplesDescriptionLocalisation",
            },
            {
              header: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
              accessorKey: "exemplaireDansEstampesMultiplesDescriptionNotes",
              cell: ({ row }: any) => {
                return (
                  <HtmlCell
                    value={
                      row.original
                        .exemplaireDansEstampesMultiplesDescriptionNotes
                    }
                  />
                );
              },
            },
          ],
          actions: [
            { name: "show", label: "télécharger" },
            { name: "delete", label: "supprimer" },
            { name: "edit", label: "modifier" },
          ],
        },
      }}
    />
  );
};

export type RadioItem = {
  value: string;
} & LabelProps;

type RadioItemProps = RadioItem &
  React.ComponentPropsWithoutRef<typeof RadioGroupItem>;

export const InnerRadioItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupItem>,
  RadioItemProps
>(({ value, label, dispLabel, ...props }, ref) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-row items-center gap-2">
      <RadioGroupItem value={value} id={value} ref={ref} {...props} />
      <Label
        htmlFor={value}
        className="text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
      >
        {dispLabel !== false && (label ?? t(value))}
      </Label>
    </div>
  );
});

export type RadioGroupProps = {
  items: RadioItem[];
  resettable?: boolean;
};
export const RadioGroup = ({ items, resettable = true }: RadioGroupProps) => {
  const { setValue, watch } = useFormContext();

  const { t } = useTranslation();

  const relatedFields = React.useMemo(
    () => items.map((i) => i.value).filter((f) => f != null),
    [items],
  );

  const fieldsValues = watch(relatedFields);

  function onSelectField(fieldName: string) {
    relatedFields.forEach((field) => {
      setValue(field, field === fieldName, { shouldDirty: true });
    });
  }

  function onReset() {
    relatedFields.forEach((f) =>
      setValue(f, false, { shouldDirty: true, shouldTouch: true }),
    );
  }

  return (
    <ShadRadioGroup
      onValueChange={onSelectField}
      className="flex flex-row w-full flex-wrap gap-x-4 gap-y-1"
    >
      {resettable === true && (
        <Button
          className="p-0F"
          variant={"link"}
          type="button"
          onClick={onReset}
        >
          {t("reset")}
        </Button>
      )}
      {items.map((item, i) => (
        <InnerRadioItem
          {...item}
          key={item.value}
          onClick={() => onSelectField(item.value)}
          checked={fieldsValues[i] ?? false}
        />
      ))}
    </ShadRadioGroup>
  );
};

export const MultiSelectField = React.forwardRef<
  React.ElementRef<typeof MultiSelect>,
  WithPartial<
    React.ComponentPropsWithoutRef<typeof MultiSelect>,
    "onValueChange"
  > & {
    name: string;
    field?: PartialFormFieldProps;
    visible?: boolean;
  } & LabelProps
>(({ name, label, dispLabel, field, visible = true, ...props }, ref) => {
  const form = useFormContext();
  const { t } = useTranslation();

  if (!visible) {
    return null;
  }

  return (
    <FormField
      {...form}
      {...field}
      name={name}
      render={({ field }) => (
        <HeadlessFormItem>
          <FormControl>
            <div className="flex flex-col gap-1">
              {dispLabel !== false && (
                <Label
                  htmlFor={field.name}
                  className="text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                >
                  {label ?? t(name)}
                </Label>
              )}
              <MultiSelect
                id={name}
                {...field}
                ref={ref}
                {...props}
                className=" bg-white"
                defaultValue={field.value ?? []}
                onValueChange={field.onChange}
                disableTagButton
              />
            </div>
          </FormControl>
        </HeadlessFormItem>
      )}
    />
  );
});

MultiSelectField.displayName = "AbstractFormMultiSelect";

export const SelectField = ({
  name,
  options,
  label,
  dispLabel,
  field,
  placeholder,
  className,
  disabled,
  visible = true,
}: {
  name: string;
  options: FieldOption[] | undefined;
  placeholder?: string;
  field?: PartialFormFieldProps;
  className?: string;
  disabled?: boolean;
  visible?: boolean;
} & LabelProps) => {
  const form = useFormContext();
  const { t } = useTranslation();

  if (!visible) {
    return null;
  }

  return (
    <FormField
      {...form}
      {...field}
      name={name}
      render={({ field }) => (
        <HeadlessFormItem>
          <FormControl>
            <div className={cn("flex flex-col gap-1", className)}>
              {dispLabel !== false && (
                <Label
                  htmlFor={name}
                  className="text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                >
                  {label ?? t(name)}
                </Label>
              )}
              <Select
                value={field.value ?? ""}
                onValueChange={field.onChange}
                disabled={disabled}
              >
                <SelectTrigger id={name} className="bg-white">
                  <SelectValue placeholder={placeholder ?? label ?? t(name)} />
                </SelectTrigger>
                <SelectContent>
                  {options?.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </FormControl>
        </HeadlessFormItem>
      )}
    />
  );
};
